import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useRef,
} from 'react'
import axios from 'axios'
import { AttendanceList, ScheduledCourse } from '../types/scheduledCourse'
import { BASE_URL, authToken } from '../../../../../../config/constants'

interface CheckInData {
    courseId: number
    userId?: number
    giftRecipientId?: number
    presence: boolean
}

interface ScheduledCoursesContextProps {
    courses: ScheduledCourse[] | null
    fetchCourses: () => Promise<void>
    revalidateCourses: () => Promise<void>
    updateCourse: (id: number, updatedCourse: UpdateCourseData) => Promise<void>
    checkIn: (data: CheckInData) => Promise<AttendanceList[] | null>
    isMutatingCourse: boolean
    isLoadingCourse: boolean
    isRevalidatingCourses: boolean
    errorCourse: string | null
}

interface UpdateCourseData extends Partial<ScheduledCourse> {
    signed_up_participants?: {
        connect?: { id: number }[]
        disconnect?: { id: number }[]
    }
    gift_recipients?: {
        connect?: { id: number }[]
        disconnect?: { id: number }[]
    }
    attendance_list?: AttendanceList[] | null
    additional_participants?: string
}

const POPULATE_PARAMS =
    'populate[signed_up_participants][fields][0]=username' +
    '&populate[signed_up_participants][fields][1]=name' +
    '&populate[signed_up_participants][fields][2]=surname' +
    '&populate[gift_recipients][fields][0]=name' +
    '&populate[gift_recipients][fields][1]=surname' +
    '&populate[status]=*'

const ScheduledCoursesContext = createContext<
    ScheduledCoursesContextProps | undefined
>(undefined)

export const ScheduledCoursesProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [courses, setCourses] = useState<ScheduledCourse[] | null>(null)
    const [isLoadingCourse, setIsLoadingCourse] = useState(false)
    const [isRevalidatingCourses, setIsRevalidatingCourses] = useState(false)
    const [isMutatingCourse, setIsMutatingCourse] = useState(false)
    const [errorCourse, setErrorCourse] = useState<string | null>(null)
    const coursesRequestRef = useRef<Promise<void> | null>(null)

    const loadCourses = async (background = false) => {
        if (coursesRequestRef.current) {
            return coursesRequestRef.current
        }

        if (background) {
            setIsRevalidatingCourses(true)
        } else {
            setIsLoadingCourse(true)
        }
        setErrorCourse(null)

        const request = (async () => {
            const response = await axios.get(
                `${BASE_URL}/api/scheduledcourses?${POPULATE_PARAMS}`,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            )
            setCourses(response.data.data)
        })()

        coursesRequestRef.current = request

        try {
            await request
        } catch (error: unknown) {
            console.error(
                background ? 'Error revalidating courses:' : 'Error fetching courses:',
                error
            )
            if (!background) {
                setErrorCourse('Failed to fetch courses.')
            }
        } finally {
            coursesRequestRef.current = null
            if (background) {
                setIsRevalidatingCourses(false)
            } else {
                setIsLoadingCourse(false)
            }
        }
    }

    const fetchCourses = async () => {
        await loadCourses(false)
    }

    const revalidateCourses = async () => {
        await loadCourses(courses !== null)
    }

    const updateCourse = async (
        id: number,
        updatedCourse: UpdateCourseData
    ) => {
        setIsMutatingCourse(true)
        setErrorCourse(null)

        try {
            const response = await axios.put(
                `${BASE_URL}/api/scheduledcourses/${id}?${POPULATE_PARAMS}`,
                { data: updatedCourse },
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            )

            if (response.data?.data) {
                setCourses(
                    (prev) =>
                        prev?.map((course) =>
                            course.id === id ? response.data.data : course
                        ) ?? null
                )
                return response.data
            }
        } catch (error: any) {
            console.error(
                'Error updating course:',
                error.response?.data || error.message
            )
            setErrorCourse('Failed to update course.')
        } finally {
            setIsMutatingCourse(false)
        }
    }

    const checkIn = async (
        data: CheckInData
    ): Promise<AttendanceList[] | null> => {
        setIsMutatingCourse(true)
        setErrorCourse(null)

        try {
            const response = await axios.post(
                `${BASE_URL}/api/check-in`,
                data,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            )

            const updatedAttendanceList = response.data?.attendance_list

            if (updatedAttendanceList) {
                setCourses(
                    (prev) =>
                        prev?.map((course) =>
                            course.id === data.courseId
                                ? {
                                      ...course,
                                      attributes: {
                                          ...course.attributes,
                                          attendance_list: updatedAttendanceList,
                                      },
                                  }
                                : course
                        ) ?? null
                )
            }

            return updatedAttendanceList
        } catch (error: any) {
            console.error(
                'Error during check-in:',
                error.response?.data || error.message
            )
            setErrorCourse('Failed to process check-in.')
            throw error
        } finally {
            setIsMutatingCourse(false)
        }
    }

    useEffect(() => {
        fetchCourses()
    }, [])

    return (
        <ScheduledCoursesContext.Provider
            value={{
                courses,
                fetchCourses,
                revalidateCourses,
                updateCourse,
                checkIn,
                isMutatingCourse,
                isLoadingCourse,
                isRevalidatingCourses,
                errorCourse,
            }}
        >
            {children}
        </ScheduledCoursesContext.Provider>
    )
}

export const useScheduledCourses = () => {
    const context = useContext(ScheduledCoursesContext)
    if (!context) {
        throw new Error(
            'useScheduledCourses must be used within a ScheduledCoursesProvider'
        )
    }
    return context
}
